import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import CardGrid from 'components/common/subcomponents/CardGrid';
import useMyLearning from '../../hooks/useMyLearning';
import { Box } from '@mui/material';
import {
  KitCard,
  KitCardBody,
  KitCardFooter,
  KitButton,
} from 'components/common/cms-kit';
import { buttonColor } from 'services/styleUtils/buttonStyleUtils';
import { isFeatureSwitchOnClientAsync } from '../../services/featureSwitchClientUtils';

const _defaultErrorMessage =
  'An unexpected error has occured. Please try again.';

const SubscriberResources = ({ module }) => {
  const { customData: _customData, fields } = module;
  const [processedResources, setProcessedResources] = useState([]);
  const [_isLoading, setIsLoading] = useState(true);

  const theme = useTheme();
  const _router = useRouter();
  const _isMyLearning = useMyLearning();
  const { data: session } = useSession();

  useEffect(() => {
    const setResourceFeatureSwitches = async () => {
      if (!fields?.resources || !session) {
        setProcessedResources([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const email = session?.user?.email?.toLowerCase() || '';
        if (!email) {
          setProcessedResources([]);
          setIsLoading(false);
          return;
        }

        const resourceResults = await Promise.all(
          fields.resources.map(async (resource) => {
            let showOnGrid = true;

            if (resource.fields.featureSwitchName) {
              try {
                const view = await isFeatureSwitchOnClientAsync(
                  resource.fields.featureSwitchName,
                  email,
                );
                showOnGrid = !!view;
              } catch (error) {
                console.error('Feature switch check failed:', error);
                showOnGrid = false;
              }
            }

            return {
              ...resource,
              fields: {
                ...resource.fields,
                showOnGrid,
              },
            };
          }),
        );

        setProcessedResources(resourceResults);
      } catch (error) {
        console.error('Error in resource feature switches:', error);
        setProcessedResources([]);
      } finally {
        setIsLoading(false);
      }
    };

    setResourceFeatureSwitches();
  }, [session, fields?.resources]);

  // Filter and sort processed resources
  const visibleResources = processedResources
    .filter((res) => res.fields.showOnGrid)
    .sort((a, b) => {
      const aSort = parseInt(a.fields.sortOrder, 10) || 0;
      const bSort = parseInt(b.fields.sortOrder, 10) || 0;
      return aSort - bSort;
    });

  return (
    <MainFrame>
      {session && (
        <HeadingCardFrame>
          <Box
            sx={{
              maxWidth: {
                sm: '540px',
                md: '868px',
                lg: '1200px',
                xl: '1200px',
              },
            }}
          >
            <TitleContainer>
              <Title style={{ color: fields.titleColor }}>{fields.title}</Title>
            </TitleContainer>
            <CardGrid
              style={{
                gap: {
                  sm: '0 .5em',
                  md: '0 .5em',
                  lg: '0 1.5em',
                  xl: '0 3.85em',
                },
                margin: {
                  sm: '5px 0 5px 0',
                  md: '7px 0 7px 0',
                  lg: '10px 0 10px -2',
                  xl: '-10px 0 10px -3.85em',
                },
              }}
            >
              {visibleResources.map((r) => (
                <KitCard
                  key={r.contentID}
                  style={{
                    width: '21rem',
                    maxHeight: '26rem',
                  }}
                >
                  <React.Fragment key={r.contentID}>
                    <KitCardBody style={{ padding: '0 1rem' }}>
                      <CardTitle style={{ minHeight: '45px' }}>
                        {r.fields.title}
                      </CardTitle>
                      <div>
                        {r.fields.cardImage && (
                          <ImageContainer
                            style={{
                              backgroundImage: `url(${r.fields.cardImage?.url})`,
                            }}
                          ></ImageContainer>
                        )}
                      </div>
                      <CardDescription>{r.fields.description}</CardDescription>
                    </KitCardBody>
                    <KitCardFooter
                      style={{
                        width: '100%',
                        height: '60px',
                        borderTop: '1px dashed #000',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        padding: '.5rem 1rem',
                      }}
                    >
                      <a
                        href={r.fields?.buttonActionURL.href}
                        target={r.fields?.buttonActionURL.target}
                      >
                        <KitButton
                          size="sm"
                          round
                          style={{
                            backgroundColor: buttonColor(
                              theme,
                              r.fields?.buttonColor,
                            ),
                            color: buttonColor(theme, r.fields.buttonTextColor),
                            border:
                              '1px solid ' +
                              buttonColor(theme, r.fields.buttonBorderColor),
                          }}
                          sx={{ width: '110px' }}
                        >
                          {r.fields.buttonText}
                        </KitButton>
                      </a>
                    </KitCardFooter>
                  </React.Fragment>
                </KitCard>
              ))}
            </CardGrid>
          </Box>
        </HeadingCardFrame>
      )}
    </MainFrame>
  );
};

const MainFrame = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  '@media screen and (min-width: 992px)': {
    width: '100%',
    padding: '25px 0 25px 0',
  },
  '@media screen and (max-width: 992px)': {
    width: '99%',
    padding: '5px 0 5px 0',
  },
});
const HeadingCardFrame = styled('div')({
  backgroundColor: 'transparent',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '0 2em',
  '@media screen and (min-width: 992px)': {
    width: '100%',
    margin: '40px 150px',
  },
  '@media screen and (max-width: 992px)': {
    width: '99%',
    margin: '10px 20px',
  },
});
const _Container = styled('div')({
  marginTop: '30px',
  paddingBottom: '50px',
  '@media screen and (min-width: 992px)': {
    width: '100%',
  },
  '@media screen and (max-width: 992px)': {
    width: '99%',
  },
});
const TitleContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
  '@media screen and (min-width: 992px)': {
    width: '100%',
    marginLeft: '60px',
  },
  '@media screen and (max-width: 992px)': {
    width: '99%',
    marginLeft: '10px',
  },
});
const Title = styled('div')({
  margin: '25px 20px 0 0',
  fontWeight: '700',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  textTransform: 'uppercase',
  '@media screen and (min-width: 992px)': {
    width: '100%',
    fontSize: '1.75rem',
    lineHeight: '2.255rem',
  },
  '@media screen and (max-width: 992px)': {
    width: '99%',
    fontSize: '1.325rem',
    lineHeight: '1.575rem',
  },
  '@media screen and (max-width: 400px)': {
    width: '99%',
    marginLeft: '35px',
  },
});
const CardTitle = styled('div')({
  margin: '15px 0 10px 0',
  fontWeight: '600',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  '@media screen and (min-width: 992px)': {
    width: '100%',
    fontSize: '1.125rem',
    lineHeight: '1.313rem',
  },
  '@media screen and (max-width: 992px)': {
    width: '99%',
    fontSize: '1.125rem',
    lineHeight: '1.313rem',
  },
});
const CardDescription = styled('div')({
  margin: '10px 0 10px 0',
  fontWeight: '400',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  '@media screen and (min-width: 992px)': {
    width: '100%',
    fontSize: '1rem',
    lineHeight: '1.375rem',
  },
  '@media screen and (max-width: 992px)': {
    width: '99%',
    fontSize: '1rem',
    lineHeight: '1.375rem',
  },
});

const ImageContainer = styled('div')(({ theme: _theme }) => ({
  width: '300px',
  height: '180px',
  backgroundSize: '100%',
  backgroundRepeat: 'no-repeat',
}));

const _IntroText = styled('div')(({ theme }) => ({
  color: theme.palette.common.black,
  fontSize: '1.063rem',
  lineHeight: '1.75rem',
  '& li': {
    '&::before': {
      backgroundColor: '#000 !important',
    },
  },
  [theme.breakpoints.up('md')]: {
    marginBottom: '10px',
  },
  [theme.breakpoints.down('md')]: {
    marginBottom: '25px',
  },
}));

export default SubscriberResources;
